import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Search, X, FileText, Wrench, BookOpen, CornerDownLeft } from "lucide-react";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useContent } from "@/content/ContentProvider";
import {
  searchEntries,
  groupResults,
  GROUP_LABELS,
  buildSearchIndex,
} from "@/search/searchIndex";
import { trackEvent } from "@/analytics/ga";

/**
 * Language-aware, route-safe site search.
 *
 * - Trigger: compact icon button in the header.
 * - Panel: top-anchored, minimal dialog with a live results list.
 * - Live suggestions appear after 3 characters.
 * - All result links navigate inside the active `/ar` or `/en` route tree.
 * - Fully keyboard accessible (↑/↓, Enter, Esc, ⌘K / Ctrl+K to open).
 */

const LABELS = {
  ar: {
    open: "بحث",
    title: "البحث في الموقع",
    placeholder: "ابحث في الصفحات والأدوات والمقالات…",
    hint: "اكتب 3 أحرف على الأقل لعرض الاقتراحات.",
    empty: "لا توجد نتائج مطابقة.",
    close: "إغلاق",
    shortcutLabel: "اختصار البحث",
    kbdHint: "اضغط Enter للفتح · Esc للإغلاق",
  },
  en: {
    open: "Search",
    title: "Search the site",
    placeholder: "Search pages, tools, and articles…",
    hint: "Type at least 3 characters to see suggestions.",
    empty: "No matching results.",
    close: "Close",
    shortcutLabel: "Search shortcut",
    kbdHint: "Press Enter to open · Esc to close",
  },
};

const TYPE_ICON = { page: FileText, tool: Wrench, article: BookOpen };

export default function SiteSearch({ triggerVariant = "desktop", testId = "site-search" }) {
  const { lang, dir } = useLanguage();
  const l = LABELS[lang];
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  // Rebuild the search index whenever the CMS-backed tools/articles change.
  // Cheap: the index is a few hundred rows of pre-normalised strings.
  const { tools, toolCategories, articles, articleCategories } = useContent();
  const liveIndex = useMemo(
    () => buildSearchIndex({ tools, toolCategories, articles, articleCategories }),
    [tools, toolCategories, articles, articleCategories]
  );

  const results = useMemo(
    () => searchEntries(query, lang, { minChars: 3, limit: 8, index: liveIndex }),
    [query, lang, liveIndex]
  );
  const grouped = useMemo(() => groupResults(results), [results]);
  const flat = results; // already ordered — keep a single flat nav order

  // Reset active index when results change
  useEffect(() => {
    setActiveIdx(0);
  }, [query, lang]);

  // Clear query when panel closes so reopening feels fresh
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Close when the language changes (URL-based) to avoid cross-locale results
  useEffect(() => {
    setOpen(false);
  }, [lang]);

  // Focus management + body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Wait a tick so the portal is mounted before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => {
        clearTimeout(t);
        // Always clear the lock (no other code path sets it in this app)
        document.body.style.overflow = "";
        // restore focus to the trigger that was used to open the panel
        triggerRef.current?.focus?.();
      };
    }
    return undefined;
  }, [open]);

  // Keep the active item in view on keyboard nav
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx, open]);

  // Global keyboard shortcuts:
  //  - Cmd/Ctrl+K toggles the panel (from anywhere)
  //  - Escape always closes the panel when it is open (regardless of focus)
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const goToResult = useCallback(
    (result) => {
      if (!result) return;
      // GA4 uses `search_term` as a standard parameter on the `search`
      // event; we use that name verbatim. `result_type` and `result_id`
      // capture the *picked* item so we can compute search-to-click CTR.
      trackEvent("site_search", {
        search_term: (query || "").trim().slice(0, 100),
        result_type: result.type,
        result_id: result.id,
        result_path: result.path,
      });
      // Build the localised target. For English the prefix is empty, so
      // a homepage hit becomes "/" (not ""), and "/tools" stays "/tools".
      const prefix = langPrefix(lang);
      const sub = result.path === "/" ? "" : result.path;
      const target = `${prefix}${sub}` || "/";
      setOpen(false);
      navigate(target);
    },
    [lang, navigate, query]
  );

  const onPanelKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (flat.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToResult(flat[activeIdx]);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(flat.length - 1);
    }
  };

  const renderTrigger = () => {
    const baseCls =
      "inline-flex items-center justify-center rounded-full sas-glass text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors";

    if (triggerVariant === "bar") {
      // Inline "search bar" trigger. Looks like a real input, but is a button
      // that opens the existing top-anchored results panel. Keyboard users can
      // still open with ⌘K/Ctrl+K exactly as before.
      return (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={l.open}
          title={l.open}
          className="group inline-flex items-center gap-2 h-9 pe-2.5 ps-3 rounded-full sas-glass hover:border-[color:var(--sas-ink)] transition-colors w-[240px] xl:w-[260px]"
          data-testid={`${testId}-trigger`}
        >
          <Search
            size={15}
            aria-hidden
            className="text-[color:var(--sas-ink-2)] group-hover:text-[color:var(--sas-ink)] shrink-0"
          />
          <span className="flex-1 truncate text-start text-[13px] text-[color:var(--sas-ink-2)] group-hover:text-[color:var(--sas-ink)]">
            {l.placeholder}
          </span>
          <span
            className="hidden xl:inline-flex items-center gap-1 text-[10.5px] font-medium text-[color:var(--sas-ink-2)] px-1.5 py-0.5 rounded border border-[color:var(--sas-line)] bg-white shrink-0"
            aria-hidden
            dir="ltr"
          >
            ⌘K
          </span>
        </button>
      );
    }

    if (triggerVariant === "mobile-bar") {
      // Mobile "search bar" trigger — visually a real, tappable input that
      // expands to fill the available header space (flex-1). Uses a short
      // placeholder so it stays readable on narrow phones, and skips the
      // ⌘K keyboard hint that wouldn't apply on touch devices.
      return (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={l.open}
          title={l.open}
          className="group inline-flex items-center gap-2 h-10 pe-3 ps-3.5 rounded-full sas-glass hover:border-[color:var(--sas-ink)] transition-colors flex-1 min-w-0"
          data-testid={`${testId}-trigger-mobile-bar`}
        >
          <Search
            size={15}
            aria-hidden
            className="text-[color:var(--sas-ink-2)] group-hover:text-[color:var(--sas-ink)] shrink-0"
          />
          <span className="flex-1 truncate text-start text-[13px] text-[color:var(--sas-ink-2)] group-hover:text-[color:var(--sas-ink)]">
            {lang === "ar" ? "ابحث…" : "Search…"}
          </span>
        </button>
      );
    }

    const sizeCls = triggerVariant === "mobile" ? "w-10 h-10" : "w-9 h-9";
    return (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label={l.open}
        title={l.open}
        className={`${baseCls} ${sizeCls}`}
        data-testid={`${testId}-trigger${triggerVariant === "mobile" ? "-mobile" : ""}`}
      >
        <Search size={triggerVariant === "mobile" ? 18 : 16} aria-hidden />
      </button>
    );
  };

  return (
    <>
      {renderTrigger()}
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <SearchPanel
            dir={dir}
            lang={lang}
            labels={l}
            query={query}
            onQueryChange={setQuery}
            results={flat}
            grouped={grouped}
            activeIdx={activeIdx}
            setActiveIdx={setActiveIdx}
            onClose={() => setOpen(false)}
            onSelect={goToResult}
            onKeyDown={onPanelKeyDown}
            inputRef={inputRef}
            listRef={listRef}
            testId={testId}
          />,
          document.body
        )}
    </>
  );
}

function SearchPanel({
  dir,
  lang,
  labels,
  query,
  onQueryChange,
  results,
  grouped,
  activeIdx,
  setActiveIdx,
  onClose,
  onSelect,
  onKeyDown,
  inputRef,
  listRef,
  testId,
}) {
  // Map a (type, indexInGroup) back to the flat navigation index
  const typeOrder = ["page", "tool", "article"];
  const indexOfResultInFlat = (result) => results.findIndex((r) => r.id === result.id);

  const showHint = query.trim().length > 0 && query.trim().length < 3;
  const showEmpty = query.trim().length >= 3 && results.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-[2px] px-4 sm:px-6 pt-20 sm:pt-24"
      dir={dir}
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
      data-testid={`${testId}-panel`}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => {
        // Click-outside-to-close: only when the mousedown target is the
        // backdrop wrapper itself (not a child of the panel).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Accessible backdrop target for tests / assistive close */}
      <button
        type="button"
        aria-label={labels.close}
        onClick={onClose}
        className="sr-only"
        data-testid={`${testId}-backdrop`}
        tabIndex={-1}
      />

      {/* Top-anchored panel */}
      <div className="relative w-full max-w-2xl">
        <div
          className="bg-white rounded-2xl border border-[color:var(--sas-line)] shadow-2xl overflow-hidden"
          style={{ boxShadow: "0 24px 60px -24px rgba(15, 15, 25, 0.25)" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Input row */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[color:var(--sas-line)]">
            <Search size={18} className="text-[color:var(--sas-ink-2)] shrink-0" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={labels.placeholder}
              aria-label={labels.title}
              dir={dir}
              lang={lang}
              autoComplete="off"
              spellCheck="false"
              className="flex-1 bg-transparent outline-none text-[15px] leading-6 text-[color:var(--sas-ink)] placeholder:text-[color:var(--sas-ink-2)]"
              data-testid={`${testId}-input`}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label={labels.close}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[color:var(--sas-ink-2)] hover:bg-[color:var(--sas-accent-soft)] hover:text-[color:var(--sas-ink)] transition-colors"
              data-testid={`${testId}-close`}
            >
              <X size={16} aria-hidden />
            </button>
          </div>

          {/* Results area */}
          <div
            ref={listRef}
            className="max-h-[60vh] overflow-y-auto"
            role="listbox"
            aria-label={labels.title}
            data-testid={`${testId}-results`}
          >
            {showHint && (
              <div
                className="px-5 py-6 text-sm text-[color:var(--sas-ink-2)]"
                data-testid={`${testId}-hint`}
              >
                {labels.hint}
              </div>
            )}
            {showEmpty && (
              <div
                className="px-5 py-6 text-sm text-[color:var(--sas-ink-2)]"
                data-testid={`${testId}-empty`}
              >
                {labels.empty}
              </div>
            )}
            {!showHint && !showEmpty && results.length > 0 && (
              <div className="py-2">
                {typeOrder.map((type) => {
                  const bucket = grouped[type] || [];
                  if (bucket.length === 0) return null;
                  const Icon = TYPE_ICON[type] || FileText;
                  const groupLabel = GROUP_LABELS[lang][type];
                  return (
                    <div key={type} className="py-1">
                      <div className="px-5 pt-3 pb-1.5 text-[11px] font-semibold tracking-wide uppercase text-[color:var(--sas-ink-2)]">
                        {groupLabel}
                      </div>
                      <ul>
                        {bucket.map((r) => {
                          const flatIdx = indexOfResultInFlat(r);
                          const isActive = flatIdx === activeIdx;
                          return (
                            <li key={r.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                data-active={isActive ? "true" : "false"}
                                onMouseEnter={() => setActiveIdx(flatIdx)}
                                onClick={() => onSelect(r)}
                                className={`w-full flex items-center gap-3 px-5 py-2.5 text-start transition-colors ${
                                  isActive
                                    ? "bg-[color:var(--sas-accent-soft)]"
                                    : "hover:bg-[color:var(--sas-accent-soft)]"
                                }`}
                                data-testid={`${testId}-result-${r.id}`}
                              >
                                <span
                                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{
                                    background: "var(--sas-accent-soft)",
                                    color: "var(--sas-accent)",
                                  }}
                                  aria-hidden
                                >
                                  <Icon size={15} />
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-[14.5px] font-medium text-[color:var(--sas-ink)] truncate">
                                    {r.title}
                                  </span>
                                  {r.subtitle && (
                                    <span className="block text-[12.5px] text-[color:var(--sas-ink-2)] truncate">
                                      {r.subtitle}
                                    </span>
                                  )}
                                </span>
                                {isActive && (
                                  <CornerDownLeft
                                    size={14}
                                    className="text-[color:var(--sas-ink-2)] shrink-0"
                                    aria-hidden
                                  />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer hint row */}
          <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-t border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/40">
            <span className="text-[11px] text-[color:var(--sas-ink-2)]">{labels.kbdHint}</span>
            <span className="text-[11px] text-[color:var(--sas-ink-2)]" dir="ltr">
              ⌘K · Ctrl+K
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
